import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search } from 'lucide-react';
import { useSearchExplorer } from '../contexts/SearchExplorerContext';
import newsService, { NewsArticle } from '../services/newsService';
import proService, { ProEvent, ProLiveData } from '../services/proService';
import { subscribeLive } from '../services/liveSocket';
import { LiveScorePacer } from '../utils/liveScorePacer';
import tournamentOpService, { LiveListEntry } from '../services/tournamentOpService';
import configService, { TOURNAMENT_SOFTWARE_ENABLED } from '../services/configService';
import forumService, { ForumTopic } from '../services/forumService';
import proRankingService from '../services/proRankingService';
import LiveTickerBar from '../components/home/LiveTickerBar';
import LiveBlogRail from '../components/home/LiveBlogRail';
import HomeNewProfiles from '../components/home/HomeNewProfiles';
import LeadStoryGrid from '../components/home/LeadStoryGrid';
import HomeLivePdcLead from '../components/home/HomeLivePdcLead';
import NeuBeiDartCloudSection from '../components/home/NeuBeiDartCloudSection';
import HomeProfileCta from '../components/home/HomeProfileCta';
import NewsFeedList from '../components/home/NewsFeedList';
import ProScoresBlock from '../components/home/ProScoresBlock';
import HomePdcCalendar from '../components/home/HomePdcCalendar';
import HomePredictionGames from '../components/home/HomePredictionGames';
import HomeNewTournaments from '../components/home/HomeNewTournaments';
import HomeUpcomingTournaments from '../components/home/HomeUpcomingTournaments';
import HomeDartsKnowledge from '../components/home/HomeDartsKnowledge';
import HomeRecentRankings from '../components/home/HomeRecentRankings';
import HomeForumTopics from '../components/home/HomeForumTopics';
import HomeNewListingGroups from '../components/home/HomeNewListingGroups';
import TopCitiesSection from '../components/places/TopCitiesSection';
import SolutionsTeaser from '../components/home/SolutionsTeaser';
import HelpArticlesTeaser from '../components/home/HelpArticlesTeaser';
import SidebarLiveTournaments from '../components/home/SidebarLiveTournaments';
import SidebarRankings, {
  SidebarRankingData,
  SIDEBAR_RANKING_MAX_TABS,
  SIDEBAR_RANKING_TOP_COUNT,
} from '../components/home/SidebarRankings';
import SidebarUpcoming from '../components/home/SidebarUpcoming';
import SidebarPrediction from '../components/home/SidebarPrediction';
import SidebarAppCta from '../components/home/SidebarAppCta';
import PostFeedList from '../components/posts/PostFeedList';
import LiveChat from '../components/livechat/LiveChat';
import HomeActivityList from '../components/home/HomeActivityList';
import { Activity } from 'lucide-react';
import { LOBBY_GLOBAL_ID } from '../services/liveChatService';
import AuthContext from '../contexts/AuthContext';
import useHomeUnreadCounts from '../hooks/useHomeUnreadCounts';
import UnreadBadge from '../components/common/UnreadBadge';
import { t } from '../i18n';

const ACCENT = '#3fced1';

/** Startseiten-Tabs: Entdecken (bisherige Startseite) | Feed | Für dich | Chat. */
type HomeTab = 'discover' | 'feed' | 'forYou' | 'chat' | 'activity';
/** So viele News laden: 5 für den Aufmacher + bis zu 9 für die Feed-Liste. */
const NEWS_LIMIT = 14;

/**
 * Startseite als Darts-Info-Plattform (Kicker/OneFootball-DNA):
 * Live-Ticker-Leiste → Aufmacher (Zeitungslayout) → Zweispalter aus
 * Hauptspalte (News-Feed, Pro-Spiele, Community) und Sidebar (Live-Turniere,
 * kommende Turniere, Tippspiel, App, Ranglisten).
 *
 * Geteilte Datenquellen (News, Pro-Events, Live-Liste, Forum) werden hier
 * EINMAL geladen und als Props verteilt — Pro-Events (Ticker + Score-Block)
 * und Live-Liste (Sidebar/Mobile-Live-Turniere) mit Silent-Refresh.
 */
const HomePage: React.FC = () => {
  const { open: openSearch } = useSearchExplorer();
  const [news, setNews] = useState<NewsArticle[]>([]);
  // CLS-Fix (2026-09-01): solange News/Pro-Events noch laden, rendern die
  // Sektionen Skeletons in Endhöhe statt null (sonst ploppt alles nach)
  const [newsLoaded, setNewsLoaded] = useState(false);
  const [proEvents, setProEvents] = useState<ProEvent[]>([]);
  const [proEventsLoaded, setProEventsLoaded] = useState(false);
  const [liveTournaments, setLiveTournaments] = useState<LiveListEntry[]>([]);
  // Feature-Flag Turniersoftware: Default true (Feature sichtbar), solange die
  // Config lädt oder der Fetch fehlschlägt
  const [tournamentSoftwareEnabled, setTournamentSoftwareEnabled] = useState(true);
  const [forumTopics, setForumTopics] = useState<ForumTopic[]>([]);
  const [rankingData, setRankingData] = useState<SidebarRankingData[]>([]);

  // Tabs "Entdecken | Feed | Für dich | Chat" (wie in der App): der
  // Entdecken-Content bleibt beim Tab-Wechsel GEMOUNTET (nur CSS hidden),
  // damit nichts neu lädt. Feed/Für dich/Chat werden erst beim ersten Besuch
  // gemountet und danach ebenfalls nur noch versteckt.
  const [homeTab, setHomeTab] = useState<HomeTab>('discover');
  const [visitedTabs, setVisitedTabs] = useState<Record<HomeTab, boolean>>({
    discover: true,
    feed: false,
    forYou: false,
    chat: false,
    activity: false,
  });

  const selectTab = (tab: HomeTab) => {
    setHomeTab(tab);
    setVisitedTabs((prev) => (prev[tab] ? prev : { ...prev, [tab]: true }));
  };

  // Ungelesen-Badges an Feed / Für dich / Chat (Marcel 2026-09-03, wie App).
  // AuthContext optional lesen: HomePage rendert auch ohne Provider (Tests).
  const authCtx = useContext(AuthContext);
  const unread = useHomeUnreadCounts(homeTab, authCtx?.user?.id ?? null);

  // 5. Tab „Aktivitäten“ nur mit Icon (Marcel 2026-10-08): gleicher Button, Icon statt Text, Label nur für Screenreader
  const homeTabButton = (key: HomeTab, label: string, icon?: React.ReactNode) => {
    const active = homeTab === key;
    return (
      <button
        type="button"
        role="tab"
        aria-selected={active}
        aria-label={icon ? label : undefined}
        title={icon ? label : undefined}
        onClick={() => selectTab(key)}
        data-testid={`home-tab-${key}`}
        className={`relative pb-2.5 pt-1 text-sm transition ${
          active ? 'font-bold text-white' : 'font-medium text-zinc-400 hover:text-white'
        }`}
      >
        {icon ?? label}
        {!active && (key === 'feed' || key === 'forYou' || key === 'chat' || key === 'activity') && (
          <UnreadBadge count={unread[key]} cap={unread.cap} className={icon ? 'absolute -right-3 -top-2' : 'absolute -right-4 -top-1.5'} />
        )}
        {active && (
          <span
            className="absolute inset-x-0 bottom-0 h-0.5 rounded-full"
            style={{ backgroundColor: ACCENT }}
          />
        )}
      </button>
    );
  };

  // News (einmalig)
  useEffect(() => {
    let cancelled = false;
    newsService
      .getLatestNews({ limit: NEWS_LIMIT })
      .then((res) => {
        if (!cancelled && res.success && res.data) {
          setNews(newsService.getSafeNewsArray(res.data));
        }
      })
      .catch(() => {
        /* Seite nicht mit Fehlern belasten */
      })
      .finally(() => {
        if (!cancelled) setNewsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Schrittmacher (2026-10-18): Live-Restpunkte/Legs im Ticker kommen per
  // Socket und werden Aufnahme für Aufnahme angezeigt (liveScorePacer.ts).
  const pacerRef = useRef<LiveScorePacer | null>(null);
  if (!pacerRef.current) {
    pacerRef.current = new LiveScorePacer((eventId, liveData) => {
      setProEvents((prev) => {
        const index = prev.findIndex((e) => proService.getEventId(e) === eventId);
        if (index === -1) return prev;
        const next = [...prev];
        next[index] = { ...next[index], liveData: liveData as ProLiveData };
        return next;
      });
    });
  }
  useEffect(() => () => pacerRef.current?.dispose(), []);

  const loadProEvents = useCallback(() => {
    return proService
      .getEventsByDateWithStats(0)
      .then((list) => {
        const safe = proService.getSafeEventsArray(Array.isArray(list) ? list : []);
        // Der Poll ist nur Sicherheitsnetz: den angezeigten Socket-Stand nicht überfahren
        const pacer = pacerRef.current;
        setProEvents(
          safe.map((e) => {
            if (!proService.isLive(e)) return e;
            const shown = pacer?.displayedFor(String(proService.getEventId(e) || ''));
            return shown ? { ...e, liveData: shown as ProLiveData } : e;
          }),
        );
      })
      .catch(() => {
        /* alte Daten behalten */
      })
      .finally(() => setProEventsLoaded(true));
  }, []);

  // Pro-Events heute (mit Silent-Refresh für Live-Scores)
  useEffect(() => {
    loadProEvents();
    const interval = window.setInterval(loadProEvents, 60_000);
    return () => window.clearInterval(interval);
  }, [loadProEvents]);

  // Socket nur, solange ein Pro-Spiel läuft (kein Dauer-Socket für jeden Startseiten-Besuch)
  const hasLiveProEvents = proEvents.some((e) => proService.isLive(e));
  useEffect(() => {
    if (!hasLiveProEvents) return undefined;
    return subscribeLive('live-games', {
      liveGameUpdate: (data: any) => {
        const eventId = data?.eventId ? String(data.eventId) : null;
        if (eventId && data?.liveData) pacerRef.current?.push(eventId, data.liveData);
      },
      liveGameStarted: () => {
        loadProEvents();
      },
      liveGameFinished: (data: any) => {
        if (data?.eventId) pacerRef.current?.reset(String(data.eventId));
        loadProEvents();
      },
    });
  }, [hasLiveProEvents, loadProEvents]);

  // Laufende Community-Turniere (mit Refresh) — nur wenn die Turniersoftware
  // per Feature-Flag aktiviert ist; sonst wird der Fetch komplett übersprungen
  useEffect(() => {
    let cancelled = false;
    let interval: number | undefined;
    const load = () => {
      tournamentOpService
        .getLiveList()
        .then((list) => {
          if (!cancelled) setLiveTournaments((list || []).filter((e) => e && e.operationId));
        })
        .catch(() => {
          /* alte Daten behalten */
        });
    };
    configService.isEnabled(TOURNAMENT_SOFTWARE_ENABLED).then((enabled) => {
      if (cancelled) return;
      setTournamentSoftwareEnabled(enabled);
      if (!enabled) return;
      load();
      interval = window.setInterval(load, 60_000);
    });
    return () => {
      cancelled = true;
      if (interval !== undefined) window.clearInterval(interval);
    };
  }, []);

  // Neueste Forum-Themen (einmalig)
  useEffect(() => {
    let cancelled = false;
    forumService
      .getTopics({ sort: 'newest', limit: 6 })
      .then((res) => {
        if (!cancelled) setForumTopics(Array.isArray(res.topics) ? res.topics : []);
      })
      .catch(() => {
        /* Block bleibt einfach ausgeblendet */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Pro-Ranglisten (einmalig): erste 2-3 Ranglisten mit Top-8-Einträgen laden;
  // das Widget selbst ist rein präsentational und wird doppelt gemountet
  // (Desktop-Sidebar + Mobile unter "Weitere News") ohne Doppel-Fetch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rankings = await proRankingService.getAllRankings();
        const sorted = rankings
          .filter((r) => r && (r._id || r.id))
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .slice(0, SIDEBAR_RANKING_MAX_TABS);
        const loaded = await Promise.all(
          sorted.map(async (ranking) => {
            // Der Entries-Endpoint erwartet die UUID `id` — die Mongo-`_id`
            // liefert 404 (deshalb blieb das Widget bisher leer)
            const id = (ranking.id || ranking._id) as string;
            try {
              const res = await proRankingService.getRankingEntries(
                id,
                SIDEBAR_RANKING_TOP_COUNT,
                0,
              );
              return { ranking, summary: res.ranking || null, entries: res.entries || [] };
            } catch {
              return { ranking, summary: null, entries: [] };
            }
          }),
        );
        if (!cancelled) {
          setRankingData(loaded.filter((d) => d.entries.length > 0));
        }
      } catch {
        /* Widget bleibt einfach ausgeblendet */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const leadArticles = news.slice(0, 5);
  const feedArticles = news.slice(5, NEWS_LIMIT);

  return (
    <div className="min-h-screen bg-zinc-950">
      <Helmet>
        <title>{t('web.page.home-page.dartcloud-suchen-finden-spielen')}</title>
        <meta
          name="description"
          content="Finde Turniere, Vereine, Spielstätten und Dart-Shops in deiner Nähe. Live-Scores, News und die Darts-Community auf DartCloud."
        />
      </Helmet>

      <h1 className="sr-only">{t('web.page.home-page.dartcloud-darts-news-live-scores')}</h1>

      {/* 4 Tabs GANZ OBEN direkt unter der Navbar (Marcel 2026-08-31/09-01):
          Entdecken | Feed | Für dich | Chat — Suche + Live-Ticker gehören zum
          Entdecken-Tab, Chat ist der globale Lobby-Live-Chat */}
      <div className="mx-auto max-w-7xl px-4 pt-2 sm:px-6">
        <div
          role="tablist"
          aria-label={t('web.page.home-page.startseiten-bereiche')}
          className="flex items-center gap-6 border-b border-zinc-800"
        >
          {homeTabButton('discover', 'Entdecken')}
          {homeTabButton('feed', 'Feed')}
          {homeTabButton('forYou', t('web.page.feed-page.fuer-dich'))}
          {homeTabButton('chat', 'Chat')}
          {homeTabButton('activity', t('web.component.home-activity-list.aktivitaeten'), <Activity size={18} strokeWidth={2.25} aria-hidden="true" />)}
        </div>
      </div>

      {/* Feed-Tabs: nach dem ersten Besuch gemountet lassen (nur verstecken) */}
      {visitedTabs.feed && (
        <div className={homeTab === 'feed' ? 'mx-auto max-w-7xl px-4 py-6 sm:px-6' : 'hidden'}>
          <PostFeedList mode="feed" />
        </div>
      )}
      {visitedTabs.forYou && (
        <div className={homeTab === 'forYou' ? 'mx-auto max-w-7xl px-4 py-6 sm:px-6' : 'hidden'}>
          <PostFeedList mode="forYou" />
        </div>
      )}
      {/* Chat-Tab: globaler Lobby-Live-Chat für die ganze Community — lazy
          (erst beim ersten Öffnen gemountet), danach nur noch versteckt,
          damit Socket-Verbindung + Verlauf beim Tab-Wechsel erhalten bleiben */}
      {visitedTabs.chat && (
        /* Mobile: full-bleed über die ganze Breite (kein Card-Look, kein
           Seitenrand) + Höhe ≈ Viewport minus Header/Tabs, damit der
           Composer immer sichtbar ist; ab sm: wie bisher als Card */
        <div
          className={homeTab === 'chat' ? 'mx-auto max-w-3xl py-4 sm:px-6' : 'hidden'}
          data-testid="home-chat"
        >
          <p className="mb-3 px-4 text-xs text-zinc-400 sm:px-0">
            {t('web.page.home-page.dartcloud-lobby-der-live-chat')}
          </p>
          <LiveChat
            entityType="lobby"
            entityId={LOBBY_GLOBAL_ID}
            fullBleed
            height="clamp(65vh, calc(100dvh - 200px), 760px)"
            emptyText={t('web.page.home-page.noch-keine-nachrichten-sag-als')}
          />
        </div>
      )}

      {/* Aktivitäten-Tab (Marcel 2026-10-08): lazy, danach nur versteckt */}
      {visitedTabs.activity && (
        <div className={homeTab === 'activity' ? 'mx-auto max-w-3xl px-4 py-6 sm:px-6' : 'hidden'} data-testid="home-activity-pane">
          <HomeActivityList />
        </div>
      )}

      {/* Entdecken = komplette bisherige Startseite; beim Tab-Wechsel NICHT
          unmounten (display:none), damit nichts neu lädt */}
      <div className={homeTab === 'discover' ? undefined : 'hidden'} data-testid="home-discover">
      {/* Suche (wie in der App; die volle Suche sitzt im Header) */}
      <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6">
        <button
          type="button"
          onClick={openSearch}
          className="mb-4 flex h-10 w-full items-center gap-2.5 rounded-xl border border-zinc-800 bg-zinc-900 px-3.5 text-left text-sm text-gray-500 transition-colors hover:border-[#3fced1]/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60 sm:max-w-md"
        >
          <Search size={16} className="shrink-0" style={{ color: ACCENT }} />
          <span className="truncate">{t('web.page.home-page.turniere-vereine-spielstaetten-finden')}</span>
        </button>
      </div>

      {/* Live-Ticker: nur Pro-/PDC-Spiele (blendet sich aus, wenn nichts läuft/ansteht) */}
      <LiveTickerBar events={proEvents} loading={!proEventsLoaded} />

      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        {/* Live-Blogs: HIER nur, solange mind. ein Blog LIVE ist — sonst
            rendert die Instanz unter "Kommende Turniere" (Marcel 2026-08-31) */}
        <LiveBlogRail placement="top" />

        {/* Neue Spieler direkt unter den Live-Blogs (Marcel 2026-08-27) */}
        <HomeNewProfiles />

        {/* Laufendes PDC-Turnier als große Karte über den News (Marcel 2026-09-17) */}
        <HomeLivePdcLead />

        {/* 3. Aufmacher (Zeitungslayout) */}
        <LeadStoryGrid articles={leadArticles} loading={!newsLoaded} />

        {/* Live-Turniere auf Mobile direkt nach dem Aufmacher */}
        {tournamentSoftwareEnabled && liveTournaments.length > 0 && (
          <div className="mt-6 lg:hidden">
            <SidebarLiveTournaments entries={liveTournaments} />
          </div>
        )}

        {/* 4. Zweispalter: Hauptspalte + Sidebar */}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:mt-8 lg:grid-cols-3 lg:gap-8">
          <div className="space-y-6 lg:col-span-2">
            {/* Profil-CTA (wie in der App zwischen Aufmacher und "Neu bei DartCloud") */}
            <HomeProfileCta />
            <NeuBeiDartCloudSection />
            <HomePredictionGames />
            <HomePdcCalendar />
            <HomeNewTournaments />
            {/* „Aus der Community“ an der alten Stelle von „Darts-Wissen“ (Marcel 2026-10-08):
                neue Beiträge, Forum-Themen, Dart-Setups, Dart-Rooms */}
            <NeuBeiDartCloudSection variant="community" />
            <HomeUpcomingTournaments />
            {/* Live-Blogs ohne laufenden Blog: hier unter den kommenden
                Turnieren statt ganz oben (Marcel 2026-08-31) */}
            <LiveBlogRail placement="below-upcoming" />
            <NewsFeedList articles={feedArticles} loading={!newsLoaded} />
            {/* Ranglisten auf Mobile direkt unter "Weitere News" */}
            {rankingData.length > 0 && (
              <div className="lg:hidden">
                <SidebarRankings rankings={rankingData} />
              </div>
            )}
            <ProScoresBlock events={proEvents} />
            {forumTopics.length > 0 && <HomeForumTopics topics={forumTopics} />}
            {/* Neue Vereine / Spielstätten / Shops ganz unten (2026-08-24) */}
            <HomeNewListingGroups />
            {/* Aktuelle Ranglisten von Listings über „Darts-Wissen“ (Marcel 2026-10-20, SEO: echte Links auf die Ranglisten-Seiten) */}
            <HomeRecentRankings />
            {/* Darts-Wissen ganz unten, unter Neue Ligen/Verbände (Marcel 2026-10-08) */}
            <HomeDartsKnowledge />
            {/* Darts in deiner Stadt: 60 Top-Orte als horizontaler Slider → /darten-in/<slug> (SEO-Einstieg, 2026-09-20/24) */}
            <TopCitiesSection limit={1000} />
            {/* Lösungen-Teaser: Vereine · Spielstätten · Anmeldung · Turniersoftware → /loesungen (2026-09-20) */}
            <SolutionsTeaser />
            {/* Hilfe & Anleitungen: ALLE Hilfecenter-Artikel als horizontale Liste → /hilfe/<slug> (Marcel 2026-10-22, nur SEO) */}
            <HelpArticlesTeaser />
          </div>

          {/* Sidebar: NICHT als Ganzes sticky — die Widgets oben (App-Karte
              etc.) scrollen normal weg, nur die Ranglisten bleiben sauber
              oben stehen. WICHTIG: KEIN self-start — die Spalte muss auf
              volle Grid-Höhe stretchen, sonst hat das sticky Kind keinen
              Laufweg und klebt nie. */}
          <aside className="space-y-6">
            {tournamentSoftwareEnabled && liveTournaments.length > 0 && (
              <div className="hidden lg:block">
                <SidebarLiveTournaments entries={liveTournaments} />
              </div>
            )}
            <SidebarUpcoming />
            <SidebarPrediction />
            <SidebarAppCta />
            {/* Ranglisten auf Desktop direkt unter der App-Karte — als
                einziges Widget sticky */}
            {rankingData.length > 0 && (
              // top-20 = Navbar-Höhe (4rem) + 1rem Luft → klebt sauber
              // UNTER der Navbar, nicht dahinter
              <div className="hidden lg:block lg:sticky lg:top-20">
                <SidebarRankings rankings={rankingData} />
              </div>
            )}
          </aside>
        </div>
      </div>
      </div>
    </div>
  );
};

export default HomePage;
